import React, { FC, ReactNode, useMemo } from 'react';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import { CompanyDto } from '@thingslog/repositories';
import { useTranslation } from 'react-i18next';
import { useModal } from '@thingslog/ui-components';
import { useCompanyUtils } from '../../../hooks/useCompanyUtils';

const EMPTY_VALUE = '-';

interface SummaryField {
  label: string;
  value: string | null | undefined;
}

interface DetailField {
  label: string;
  value: string | null | undefined;
}

const CompanyInfoModal: FC<CompanyInfoModalProps> = ({ company }: CompanyInfoModalProps) => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const { getSolutionTypeLabel } = useCompanyUtils();

  const info = company.companyInfo;

  const summaryFields = useMemo(
    (): SummaryField[] => [
      { label: t('company_manager_account'), value: company.name },
      { label: t('companies_table_name'), value: info?.companyName },
      {
        label: t('company_manager_solution_type'),
        value: getSolutionTypeLabel(company.solution),
      },
    ],
    [company.name, company.solution, getSolutionTypeLabel, info?.companyName, t]
  );

  const detailFields = useMemo(
    (): DetailField[] => [
      { label: t('accounts_externalNumber'), value: company.accountExtNumber },
      { label: t('company_manager_vat_number'), value: info?.vatNumber },
      { label: t('company_manager_address'), value: info?.address },
      { label: t('company_manager_city'), value: info?.city },
      { label: t('company_manager_zip_code'), value: info?.zipCode },
      { label: t('company_manager_municipality'), value: info?.municipality },
      { label: t('company_manager_area'), value: info?.area },
      { label: t('company_manager_country'), value: info?.country },
    ],
    [company.accountExtNumber, info, t]
  );

  const hasMeaningfulInfo =
    summaryFields
      .slice(1)
      .some((field: { value: string | null | undefined }): boolean => hasValue(field.value)) ||
    detailFields.some((field: { value: string | null | undefined }): boolean =>
      hasValue(field.value)
    );

  return (
    <Box className="w-[560px] max-w-full space-y-5">
      <Paper elevation={0} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <Grid container spacing={2}>
          {summaryFields.map(
            (field: SummaryField): ReactNode => (
              <Grid item xs={12} sm={4} key={field.label}>
                <Typography variant="caption" color="textSecondary">
                  {field.label}
                </Typography>
                <Typography variant="subtitle1" className="break-words">
                  {formatValue(field.value)}
                </Typography>
              </Grid>
            )
          )}
        </Grid>
      </Paper>

      {hasMeaningfulInfo ? (
        <Paper elevation={0} className="rounded-xl border border-slate-200 p-2">
          <Grid container spacing={2}>
            {detailFields.map(
              (field: DetailField): ReactNode => (
                <Grid item xs={12} sm={6} key={field.label}>
                  <Box className="h-full rounded-lg border border-slate-100 bg-white px-4 py-3">
                    <Typography variant="caption" color="textSecondary">
                      {field.label}
                    </Typography>
                    <Typography variant="body2" className="mt-1 break-words">
                      {formatValue(field.value)}
                    </Typography>
                  </Box>
                </Grid>
              )
            )}
          </Grid>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center"
        >
          <Typography variant="body2" color="textSecondary">
            {t('accounts_info_unavailable')}
          </Typography>
        </Paper>
      )}

      <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" color="primary" onClick={closeModal}>
          {t('close_btn')}
        </Button>
      </Box>
    </Box>
  );
};

const hasValue = (value?: string | null): boolean => {
  return Boolean(value && value.trim() !== '');
};

const normalizeValue = (value?: string | null): string => {
  return hasValue(value) ? value!.trim() : EMPTY_VALUE;
};

const formatValue = (value?: string | null): string => {
  return normalizeValue(value);
};

interface CompanyInfoModalProps {
  company: CompanyDto;
}

export default CompanyInfoModal;
